import { ComponentType, LazyExoticComponent, lazy } from 'react';

/**
 * The `React.lazy` equivalent of a component type, preserving its props so
 * route elements stay type-checked at the call site.
 */
type LazyComponentOf<TExport> =
  TExport extends ComponentType<infer TProps>
    ? LazyExoticComponent<ComponentType<TProps>>
    : never;

/**
 * `React.lazy` for a module's *named* export.
 *
 * Route tables reference pages by name (`ReportingDashboardPage`), but
 * `React.lazy` wants a module whose default export is the component. This
 * bridges the two so a route can defer a page's module without that page
 * having to change how it exports itself.
 *
 * Every route element built this way must sit under a `<Suspense>` boundary —
 * the route table only holds the loader, so the chunk is fetched on the first
 * render of that route rather than at app startup.
 *
 * @example
 * const ReportingDashboardPage = lazyRoute(
 *   'ReportingDashboardPage',
 *   () => import('./routes/reporting/ReportingDashboardPage'),
 * );
 */
export const lazyRoute = <TModule extends object, TName extends keyof TModule>(
  exportName: TName,
  load: () => Promise<TModule>,
): LazyComponentOf<TModule[TName]> =>
  lazy(async () => {
    const module = await load();

    // A resolved-but-empty module means a chunk fetch failed and a listener
    // preventDefault()ed Vite's `vite:preloadError` — Vite then swallows the
    // rejection and the import() resolves `undefined`. The only listener that
    // does that (chunk-reload-recovery) reloads the page in the same tick, so
    // suspend forever and let the route hold its Suspense fallback for the
    // moment the reload needs. Reading the export instead would throw a
    // TypeError into the error boundary — a phantom crash (Sentry event,
    // PostHog event, report dialog) racing a reload that recovers on its own.
    if (!module) {
      return new Promise<never>(() => undefined);
    }

    return {
      default: module[exportName] as ComponentType<Record<string, unknown>>,
    };
  }) as LazyComponentOf<TModule[TName]>;
